import { useMemo } from 'react'

import {
  matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { SmsLogModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['smsLogs']

type ApiLogsFilters = {
  api_provider?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: ApiLogsFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useSmsLogs(options: UseEntitiesOptions = {}) {
  const { filters = {} } = options
  const {
    api_provider: filterApiProvider,
  } = filters

  const {
    updatedEntities: { smsLogs: smsLogsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const smsLogs = useSelector(reduxState => reduxState.entities.smsLogs)

  const filteredSmsLogs = useMemo((): SmsLogModel[] => {
    const filtered = Object.values(smsLogs).filter((smsLog) => {
      const {
        api_provider,
      } = smsLog

      const matchApiProvider = matchFilterString(api_provider, filterApiProvider)

      return matchApiProvider
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [smsLogsUpdatedAt, JSON.stringify(filters)])

  const filteredSmsLogsCount = filteredSmsLogs.length
  const hasFilteredSmsLogs = !!filteredSmsLogsCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredSmsLogsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingSmsLogs } = useReduxAction(
    'smsLogs',
    'loadSmsLogs',
    {
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredSmsLogs,
    filteredSmsLogsCount,
    hasFilteredSmsLogs,
    loading: loadingSmsLogs,
  }
}

export default useSmsLogs
